import React, { useMemo, useState } from 'react';

import { Popover, Tooltip } from 'antd';
import { Check, Filter } from 'lucide-react';

import type { PointsSessionUsageItem } from '@/types';

import { normalizeSessionTitle } from '../utils/pointsHistoryDisplay';

/** 与后端 PointsUsageStatsService._SESSIONS_QUERY_LIMIT 对齐，达到上限时提示列表被截断 */
const SESSION_OPTIONS_LIMIT = 500;
/** 选项较多时才展示搜索框，避免少量选项下多一层干扰 */
const SEARCH_VISIBLE_THRESHOLD = 10;

interface SessionIdFilterPopoverProps {
  value: string;
  options: PointsSessionUsageItem[];
  disabled?: boolean;
  optionsLoading?: boolean;
  onChange: (value: string) => void;
}

const SessionIdFilterPopover: React.FC<SessionIdFilterPopoverProps> = ({
  value,
  options,
  disabled = false,
  optionsLoading = false,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const hasAppliedFilter = Boolean(value);
  const truncated = options.length >= SESSION_OPTIONS_LIMIT;

  // 零消耗会话筛出来必然为空，不进入选项；按消耗降序便于定位高消耗会话
  const usableOptions = useMemo(() => {
    return options
      .filter((option) => option.session_id && option.consumed_points !== 0)
      .sort((left, right) => right.consumed_points - left.consumed_points);
  }, [options]);

  const visibleOptions = useMemo(() => {
    const normalized = keyword.trim().toLowerCase();
    if (!normalized) return usableOptions;
    return usableOptions.filter((option) => {
      return (
        option.session_id.toLowerCase().includes(normalized) ||
        (option.title ?? '').toLowerCase().includes(normalized)
      );
    });
  }, [keyword, usableOptions]);

  const showSearch = usableOptions.length > SEARCH_VISIBLE_THRESHOLD;

  const handleOpenChange = (nextOpen: boolean) => {
    if (disabled) return;
    if (!nextOpen) setKeyword('');
    setOpen(nextOpen);
  };

  const handleSelect = (optionValue: string) => {
    if (disabled || optionsLoading) return;
    if (optionValue !== value) {
      onChange(optionValue);
    }
    setKeyword('');
    setOpen(false);
  };

  const renderOption = (
    optionValue: string,
    primaryText: string,
    secondaryText: string,
  ): React.ReactNode => {
    const selected = value === optionValue;
    return (
      <label
        key={optionValue || 'all'}
        className={`flex min-h-10 cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors duration-base ${
          selected ? 'bg-theme-color-10 text-theme-color' : 'text-dark-text hover:bg-theme-color-10'
        }`}
      >
        <input
          type="radio"
          name="points-history-session-filter"
          value={optionValue}
          checked={selected}
          onChange={() => handleSelect(optionValue)}
          className="peer sr-only"
        />
        <span
          aria-hidden="true"
          className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors duration-base peer-focus-visible:ring-2 peer-focus-visible:ring-theme-color/30 peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-dark-card ${
            selected
              ? 'border-theme-color bg-theme-color text-white'
              : 'border-dark-text-secondary/50 bg-dark-bg text-transparent'
          }`}
        >
          <Check className="h-3 w-3" strokeWidth={3} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate" title={primaryText}>
            {primaryText}
          </span>
          {secondaryText ? (
            <span className="mt-0.5 block truncate text-[11px] text-dark-text-secondary">
              {secondaryText}
            </span>
          ) : null}
        </span>
      </label>
    );
  };

  const content = (
    <div className="w-72" data-testid="points-history-session-filter-panel">
      {showSearch ? (
        <div className="border-b border-dark-border p-2">
          <input
            type="text"
            value={keyword}
            aria-label="搜索会话"
            placeholder="搜索会话 ID 或标题"
            onChange={(event) => setKeyword(event.target.value)}
            className="h-8 w-full rounded-lg border border-dark-border bg-dark-bg px-2 text-xs text-dark-text outline-none transition-colors focus:border-theme-color"
          />
        </div>
      ) : null}
      <div className="max-h-60 overflow-y-auto p-2" role="radiogroup" aria-label="按会话筛选">
        {optionsLoading ? (
          <div className="space-y-2 px-2 py-1.5" aria-label="会话列表加载中">
            <div className="skeleton-block h-8 rounded-lg" />
            <div className="skeleton-block h-8 rounded-lg" />
            <div className="skeleton-block h-8 rounded-lg" />
          </div>
        ) : (
          <>
            {renderOption('', '全部', '')}
            {visibleOptions.map((option) => {
              const points = `${option.consumed_points} 积分`;
              const title = normalizeSessionTitle((option.title ?? '').trim());
              const primary = `${option.session_id} · ${points}`;
              return title
                ? renderOption(option.session_id, primary, title)
                : renderOption(option.session_id, primary, '');
            })}
            {visibleOptions.length === 0 ? (
              <p className="px-3 py-2 text-xs text-dark-text-secondary">
                {usableOptions.length === 0 ? '该项目暂无消耗会话' : '没有匹配的会话'}
              </p>
            ) : null}
          </>
        )}
      </div>
      {truncated ? (
        <p className="border-t border-dark-border px-3 py-2 text-[11px] text-dark-text-secondary">
          仅显示最近 {SESSION_OPTIONS_LIMIT} 个会话
        </p>
      ) : null}
    </div>
  );

  return (
    <Tooltip title={disabled ? '请先筛选项目' : undefined} mouseEnterDelay={0}>
      <span className="inline-flex">
        <Popover
          open={open}
          trigger="click"
          placement="bottom"
          arrow={false}
          destroyOnHidden
          content={content}
          onOpenChange={handleOpenChange}
          classNames={{ root: 'points-history-project-filter-popover' }}
        >
          <button
            type="button"
            disabled={disabled}
            data-testid="points-history-session-filter"
            aria-label={hasAppliedFilter ? '按会话筛选，已筛选' : '按会话筛选'}
            aria-expanded={open}
            aria-busy={optionsLoading}
            className={`relative inline-flex h-3 w-3 shrink-0 items-center justify-center transition-colors duration-base after:absolute after:-inset-3.5 focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-theme-color/30 disabled:cursor-not-allowed disabled:opacity-30 disabled:saturate-0 ${
              hasAppliedFilter ? 'text-theme-color' : 'text-dark-text-secondary'
            }`}
          >
            <Filter className="h-3 w-3" strokeWidth={1.8} aria-hidden="true" />
          </button>
        </Popover>
      </span>
    </Tooltip>
  );
};

export default SessionIdFilterPopover;
